import { forwardRef } from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { cx } from './cx'
import { FieldLabel, FieldMessages, useFieldIds } from './fieldChrome'

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'aria-describedby'> {
  readonly label: string
  /** Visually hide the label (still exposed to assistive tech) for dense
   * contexts where a visible label would be redundant. Never drop it: a bare
   * unlabeled textarea is the a11y review comment this primitive prevents. */
  readonly hideLabel?: boolean
  /** Inline error message — red border + message + `aria-invalid`, mirroring
   * the Input primitive. Presence alone drives the error visual/ARIA state. */
  readonly error?: string | undefined
  readonly helpText?: string | undefined
  /** Visual `*` + `aria-required` + "(required)" in the accessible label —
   * never the asterisk glyph alone (accessibility-wcag skill). */
  readonly required?: boolean
}

/**
 * Multi-line text input primitive (UID-229), the `<textarea>` sibling of the
 * Input primitive — same field structure (label / required / error / help +
 * ARIA), so the ~6 hand-rolled `<textarea>`s (RFQ notes, targeting notes,
 * copy-from-Excel, new-workspace description, part config/revision notes) get
 * one consistent, a11y-tested control instead of each re-implementing the
 * label/help/error markup.
 *
 * Forwards its ref to the underlying `<textarea>` for submit-with-errors
 * focus management parity with Input.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hideLabel = false, error, helpText, required, className, rows = 4, ...rest },
  ref,
) {
  const { id: textareaId, errorId, helpId, describedBy } = useFieldIds(rest.id, { error, helpText })

  return (
    <div className={cx('field', className)}>
      <FieldLabel htmlFor={textareaId} hideLabel={hideLabel} required={required}>
        {label}
      </FieldLabel>
      <textarea
        {...rest}
        ref={ref}
        id={textareaId}
        rows={rows}
        className={cx('textarea-control', error && 'textarea-control-error')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
      />
      <FieldMessages ids={{ errorId, helpId }} error={error} helpText={helpText} />
    </div>
  )
})
